import { useEffect, useRef, useState } from 'react'
import { Form, useNavigate, useSearchParams } from 'react-router'
import { z } from 'zod'

import { Page } from '#app/components/page.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Divider } from '#app/components/ui/divider.tsx'
import { type Route } from '#app/routes/mocra+/_form+/+types/training-documentation.ts'
import { documentLibraryDefinition } from '#app/routes/mocra+/_form+/components/document-libraries/definition.ts'
import { DocumentLibraryEditOverlay } from '#app/routes/mocra+/_form+/components/document-libraries/edit.tsx'
import {
	DocumentLibrarySchema,
	type DocumentLibraryEntry,
} from '#app/routes/mocra+/_form+/components/document-libraries/schema.ts'
import {
	createDocumentLibraryDraft,
	sanitizeDocumentLibraryDraft,
	documentLibraryDraftToEntry,
	documentLibraryEntryToDraft as ignoredDocumentLibraryEntryToDraft,
	type DocumentLibraryDraft,
	isDocumentLibraryScopeValue,
	isDocumentLibrarySystemValue,
} from '#app/routes/mocra+/_form+/components/document-libraries/types.ts'
import { trainingProgramDefinition } from '#app/routes/mocra+/_form+/components/training-programs/definition.ts'
import { TrainingProgramEditOverlay } from '#app/routes/mocra+/_form+/components/training-programs/edit.tsx'
import {
	TrainingProgramSchema,
	type TrainingProgramEntry,
} from '#app/routes/mocra+/_form+/components/training-programs/schema.ts'
import {
	createTrainingProgramDraft,
	sanitizeTrainingProgramDraft,
	trainingProgramDraftToEntry,
	trainingProgramEntryToDraft as ignoredTrainingProgramEntryToDraft,
	type TrainingProgramDraft,
	isTrainingAudienceValue,
	isTrainingCadenceValue,
	isTrainingDeliveryValue,
} from '#app/routes/mocra+/_form+/components/training-programs/types.ts'
import {
	buildStepPathFromId,
	getNextFormStepId,
} from '#app/routes/mocra+/_form+/form-steps.ts'
import { useEntityCollection } from '#app/routes/mocra+/_form+/hooks/use-entity-collection.ts'
import { useSectionForm } from '#app/routes/mocra+/_form+/hooks/use-section-form.ts'
import { TRAINING_RECORD_RETENTION_OPTIONS } from '#app/routes/mocra+/_form+/intake-options.ts'
import { createRouteSectionResources } from '#app/routes/mocra+/_form+/section-resources.ts'
import { DocumentLibrariesSection } from '#app/routes/mocra+/_form+/training-documentation/document-libraries-section.tsx'
import { RecordRetentionSection } from '#app/routes/mocra+/_form+/training-documentation/record-retention-section.tsx'
import { TrainingProgramsSection } from '#app/routes/mocra+/_form+/training-documentation/training-programs-section.tsx'

export const meta: Route.MetaFunction = () => [
	{ title: 'MoCRA — Training & Docs' },
]

const SECTION_KEY = 'trainingDocumentation'
const STEP_ID = 'training-documentation'
const TRAINING_DOCUMENTATION_RESOURCES = createRouteSectionResources({
	route: '/mocra/training-documentation',
})

const TrainingDocumentationSchema = z.object({
	documentLibraries: z.array(DocumentLibrarySchema).default([]),
	trainingPrograms: z.array(TrainingProgramSchema).default([]),
	recordRetention: z.enum(
		TRAINING_RECORD_RETENTION_OPTIONS.map((option) => option.value) as [
			(typeof TRAINING_RECORD_RETENTION_OPTIONS)[number]['value'],
			...string[],
		],
	),
})

export type TrainingDocumentationFormValues = z.infer<
	typeof TrainingDocumentationSchema
>

const DEFAULT_VALUES: TrainingDocumentationFormValues = {
	documentLibraries: [],
	trainingPrograms: [],
	recordRetention: TRAINING_RECORD_RETENTION_OPTIONS[0]?.value ?? 'six_years',
}

export default function TrainingDocumentation() {
	const navigate = useNavigate()
	const [searchParams] = useSearchParams()
	const form = useSectionForm({
		section: SECTION_KEY,
		schema: TrainingDocumentationSchema,
		defaultValue: DEFAULT_VALUES,
		onValidSubmit: () => {
			const nextStepId = getNextFormStepId(STEP_ID)
			if (!nextStepId) return
			const nextHref = buildStepPathFromId(nextStepId, searchParams)
			void navigate(nextHref)
		},
	})
	const {
		values,
		setValues,
		setFieldValue,
		getFieldErrors,
		submit,
		status,
		hasStoredValue,
		clear,
	} = form

	const documentLibrariesCollection = useEntityCollection<
		TrainingDocumentationFormValues,
		DocumentLibraryEntry,
		DocumentLibraryDraft
	>({
		form,
		fieldPath: 'documentLibraries',
		definition: documentLibraryDefinition,
		getFieldErrorPaths: getDocumentLibraryFieldErrorPaths,
	})
	const trainingProgramsCollection = useEntityCollection<
		TrainingDocumentationFormValues,
		TrainingProgramEntry,
		TrainingProgramDraft
	>({
		form,
		fieldPath: 'trainingPrograms',
		definition: trainingProgramDefinition,
		getFieldErrorPaths: getTrainingProgramFieldErrorPaths,
	})

	const didNormalizeRef = useRef(false)
	useEffect(() => {
		if (didNormalizeRef.current) return
		const normalized = normalizeTrainingDocumentationDraft(values)
		if (normalized && !trainingDocumentationValuesEqual(normalized, values)) {
			didNormalizeRef.current = true
			setValues(normalized)
			return
		}
		didNormalizeRef.current = true
		if (
			!normalized &&
			!trainingDocumentationValuesEqual(DEFAULT_VALUES, values)
		) {
			setValues(DEFAULT_VALUES)
		}
	}, [setValues, values])

	const documentLibraryItems = documentLibrariesCollection.items.map(
		(item) => ({
			draft: item.draft,
			errors: item.errors,
		}),
	)
	const trainingProgramItems = trainingProgramsCollection.items.map((item) => ({
		draft: item.draft,
		errors: item.errors,
	}))

	const [editingLibraryIndex, setEditingLibraryIndex] = useState<number | null>(
		null,
	)
	const [editingProgramIndex, setEditingProgramIndex] = useState<number | null>(
		null,
	)

	const editingLibraryDraft =
		editingLibraryIndex !== null &&
		documentLibrariesCollection.items[editingLibraryIndex]
			? documentLibrariesCollection.items[editingLibraryIndex].draft
			: documentLibrariesCollection.createDraft()

	const editingProgramDraft =
		editingProgramIndex !== null &&
		trainingProgramsCollection.items[editingProgramIndex]
			? trainingProgramsCollection.items[editingProgramIndex].draft
			: trainingProgramsCollection.createDraft()

	const handleAddLibrary = (draft: DocumentLibraryDraft) => {
		documentLibrariesCollection.append(draft)
	}

	const handleEditLibrary = (draft: DocumentLibraryDraft) => {
		if (editingLibraryIndex === null) return
		documentLibrariesCollection.replace(editingLibraryIndex, draft)
		setEditingLibraryIndex(null)
	}

	const handleRemoveLibrary = (index: number) => {
		documentLibrariesCollection.remove(index)
		if (editingLibraryIndex === index) {
			setEditingLibraryIndex(null)
		}
	}

	const handleAddProgram = (draft: TrainingProgramDraft) => {
		trainingProgramsCollection.append(draft)
	}

	const handleEditProgram = (draft: TrainingProgramDraft) => {
		if (editingProgramIndex === null) return
		trainingProgramsCollection.replace(editingProgramIndex, draft)
		setEditingProgramIndex(null)
	}

	const handleRemoveProgram = (index: number) => {
		trainingProgramsCollection.remove(index)
		if (editingProgramIndex === index) {
			setEditingProgramIndex(null)
		}
	}

	const handleRecordRetentionChange = (
		value: TrainingDocumentationFormValues['recordRetention'],
	) => {
		setFieldValue('recordRetention', value)
	}

	return (
		<Page
			title="Training & Docs"
			description="Capture the systems that govern controlled documents, how you deliver compliance training, and highlights that shape the SOP narrative."
			className="space-y-8"
		>
			<Form
				method="post"
				className="space-y-8"
				onSubmit={(event) => {
					event.preventDefault()
					void submit()
				}}
			>
				<Divider />

				<DocumentLibrariesSection
					resources={TRAINING_DOCUMENTATION_RESOURCES}
					libraries={documentLibraryItems}
					onAdd={handleAddLibrary}
					onEdit={(index) => setEditingLibraryIndex(index)}
					onRemove={handleRemoveLibrary}
				/>
				<DocumentLibraryEditOverlay
					open={editingLibraryIndex !== null}
					onOpenChange={(open) => {
						if (!open) setEditingLibraryIndex(null)
					}}
					onSubmit={handleEditLibrary}
					initialValue={editingLibraryDraft}
					onClosed={() => setEditingLibraryIndex(null)}
				/>

				<Divider />

				<TrainingProgramsSection
					resources={TRAINING_DOCUMENTATION_RESOURCES}
					programs={trainingProgramItems}
					onAdd={handleAddProgram}
					onEdit={(index) => setEditingProgramIndex(index)}
					onRemove={handleRemoveProgram}
				/>
				<TrainingProgramEditOverlay
					open={editingProgramIndex !== null}
					onOpenChange={(open) => {
						if (!open) setEditingProgramIndex(null)
					}}
					onSubmit={handleEditProgram}
					initialValue={editingProgramDraft}
					onClosed={() => setEditingProgramIndex(null)}
				/>

				<Divider />

				<RecordRetentionSection
					resources={TRAINING_DOCUMENTATION_RESOURCES}
					value={values.recordRetention}
					errors={getFieldErrors('recordRetention')}
					onChange={handleRecordRetentionChange}
				/>

				{status === 'invalid' ? (
					<p className="text-destructive text-sm">
						Check the highlighted fields before continuing.
					</p>
				) : null}

				<div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
					{hasStoredValue ? (
						<Button type="button" variant="outline" onClick={() => clear()}>
							Clear answers
						</Button>
					) : (
						<span />
					)}
					<Button type="submit" className="w-full sm:w-auto">
						Continue
					</Button>
				</div>
			</Form>
		</Page>
	)
}

function getDocumentLibraryFieldErrorPaths(index: number): readonly string[] {
	const prefix = `documentLibraries.${index}`
	return [prefix, `${prefix}.system`, `${prefix}.scope`, `${prefix}.location`]
}

function getTrainingProgramFieldErrorPaths(index: number): readonly string[] {
	const prefix = `trainingPrograms.${index}`
	return [
		prefix,
		`${prefix}.audience`,
		`${prefix}.delivery`,
		`${prefix}.cadence`,
		`${prefix}.platform`,
		`${prefix}.tracksCompletion`,
	]
}

function normalizeTrainingDocumentationDraft(
	value: unknown,
): TrainingDocumentationFormValues | null {
	if (!value || typeof value !== 'object') return null
	const record = value as Record<string, unknown>
	const librariesRaw = Array.isArray(record.documentLibraries)
		? record.documentLibraries
		: []
	const libraries = librariesRaw
		.map((entry) => documentLibraryEntryToDraftLike(entry))
		.filter(
			(
				entry,
			): entry is TrainingDocumentationFormValues['documentLibraries'][number] =>
				entry !== null,
		)
	const programsRaw = Array.isArray(record.trainingPrograms)
		? record.trainingPrograms
		: []
	const programs = programsRaw
		.map((entry) => trainingProgramEntryToDraftLike(entry))
		.filter(
			(
				entry,
			): entry is TrainingDocumentationFormValues['trainingPrograms'][number] =>
				entry !== null,
		)
	const recordRetentionRaw =
		typeof record.recordRetention === 'string'
			? record.recordRetention
			: DEFAULT_VALUES.recordRetention
	const recordRetention = TRAINING_RECORD_RETENTION_OPTIONS.some(
		(option) => option.value === recordRetentionRaw,
	)
		? recordRetentionRaw
		: DEFAULT_VALUES.recordRetention

	const candidate: TrainingDocumentationFormValues = {
		documentLibraries: libraries,
		trainingPrograms: programs,
		recordRetention,
	}
	const parsed = TrainingDocumentationSchema.safeParse(candidate)
	return parsed.success ? parsed.data : null
}

function trainingDocumentationValuesEqual(
	left: TrainingDocumentationFormValues,
	right: TrainingDocumentationFormValues,
): boolean {
	return JSON.stringify(left) === JSON.stringify(right)
}

function documentLibraryEntryToDraftLike(
	input: unknown,
): TrainingDocumentationFormValues['documentLibraries'][number] | null {
	if (!input || typeof input !== 'object') return null
	const record = input as Record<string, unknown>
	const draft = sanitizeDocumentLibraryDraft(
		createDocumentLibraryDraft({
			system: isDocumentLibrarySystemValue(record.system)
				? record.system
				: undefined,
			scope: isDocumentLibraryScopeValue(record.scope)
				? record.scope
				: undefined,
			location: typeof record.location === 'string' ? record.location : '',
		}),
	)
	return documentLibraryDraftToEntry(draft)
}

function trainingProgramEntryToDraftLike(
	input: unknown,
): TrainingDocumentationFormValues['trainingPrograms'][number] | null {
	if (!input || typeof input !== 'object') return null
	const record = input as Record<string, unknown>
	const draft = sanitizeTrainingProgramDraft(
		createTrainingProgramDraft({
			audience: isTrainingAudienceValue(record.audience)
				? record.audience
				: undefined,
			delivery: isTrainingDeliveryValue(record.delivery)
				? record.delivery
				: undefined,
			cadence: isTrainingCadenceValue(record.cadence)
				? record.cadence
				: undefined,
			platform: typeof record.platform === 'string' ? record.platform : '',
			tracksCompletion: Boolean(record.tracksCompletion),
		}),
	)
	return trainingProgramDraftToEntry(draft)
}
